/*
 * Client-side hook to resolve SEO URLs to category IDs
 * Resolves categoryId from SEO URL parameters when not in SSR
 */

import {useQuery} from '@tanstack/react-query'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getSiteByReference} from '@salesforce/retail-react-app/app/utils/site-utils'

const getIntlLocaleId = (siteId, locale) => {
    const site = getSiteByReference(siteId)
    const supportedLocale = site?.l10n?.supportedLocales?.find(
        (candidate) => candidate.alias === locale
    )
    return supportedLocale?.isSupportedByESW ? supportedLocale.id : null
}

/**
 * Resolves SEO URL segment to category ID using Commerce Cloud Shopper SEO API
 */
async function resolveCategoryId(locale, brandId, categoryPath, accessToken, appOrigin) {
    const urlSegment = `${brandId}/${categoryPath}`
    const config = getConfig()

    // Use same configuration as SSR
    const proxyPath = config.app.commerceAPI.proxyPath
    const organizationId = config.app.commerceAPI.parameters.organizationId
    const siteId = config.app.commerceAPI.parameters.siteId

    const localeId = getIntlLocaleId(siteId, locale)

    const buildUrl = (localeParam) =>
        `${appOrigin}${proxyPath}/site/shopper-seo/v1/organizations/${organizationId}/url-mapping?siteId=${siteId}${
            localeParam ? `&locale=${localeParam}` : ''
        }&urlSegment=${encodeURIComponent(urlSegment)}`

    const fetchMapping = (localeParam) =>
        fetch(buildUrl(localeParam), {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`
            }
        })

    let response = await fetchMapping(localeId)
    if (!response.ok && response.status === 404 && localeId) {
        response = await fetchMapping(null)
    }

    if (!response.ok) {
        throw new Error(`Failed to resolve SEO URL: ${response.status}`)
    }

    const data = await response.json()

    if (data.resourceType === 'category' && data.resourceId) {
        return data.resourceId
    }

    return null
}

/**
 * Hook to resolve categoryId from SEO URL parameters
 * Only runs when we have SEO URL params and no server-side data
 */
export const useClientCategoryId = (locale, brandId, categoryPath, skipClientResolve = false) => {
    const {getTokenWhenReady} = useAccessToken()
    const appOrigin = useAppOrigin()
    const shouldRun = !!(brandId && categoryPath && !skipClientResolve)

    return useQuery({
        queryKey: ['client-category-id', locale, brandId, categoryPath],
        queryFn: async () => {
            const accessToken = await getTokenWhenReady()
            return resolveCategoryId(locale, brandId, categoryPath, accessToken, appOrigin)
        },
        enabled: shouldRun,
        staleTime: 0, // No cache - always fresh request
        cacheTime: 0,
        retry: 1
    })
}
